/**
 * Nexus Billing Database Operations
 *
 * Handles all database operations for Nexus subscriptions, usage tracking,
 * and billing management.
 *
 * Primary responsibilities:
 * - Subscription CRUD operations
 * - Usage tracking and limits
 * - Customer management
 * - Analytics and reporting
 *
 * Dependencies:
 * - Supabase client (service role on server, browser on client)
 * - Nexus config constants
 * - Type definitions
 */

import { createServiceClient } from "@/lib/supabase/service-client"
import type { SupabaseClient } from "@supabase/supabase-js"
import type { Database } from "@/supabase/types"
import {
  NEXUS_PLAN_STARTER,
  NEXUS_PLAN_PROFESSIONAL,
  NEXUS_PLAN_ENTERPRISE,
  NEXUS_PRICING
} from "@/lib/stripe/nexus-config"

export interface NexusSubscription {
  id: string
  user_id: string
  stripe_subscription_id: string
  stripe_customer_id: string
  stripe_price_id: string
  plan_type: 'starter' | 'professional' | 'enterprise'
  billing_interval: 'monthly' | 'yearly'
  status: 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'trialing' | 'unpaid'
  current_period_start: string
  current_period_end: string
  cancel_at_period_end: boolean
  calls_used_this_month: number
  calls_limit: number | null
  last_reset_date: string
  trial_start_date?: string | null
  trial_end_date?: string | null
  is_trial?: boolean
  credits_used_this_month?: number
  credits_limit?: number | null
  credits_last_reset_date?: string | null
  wallet_balance_cents?: number
  wallet_auto_load_enabled?: boolean
  wallet_auto_load_threshold_cents?: number
  wallet_auto_load_amount_cents?: number
  wallet_last_auto_load_at?: string | null
  created_at: string
  updated_at: string
  metadata: Record<string, any>
}

export interface NexusUsageStats {
  has_subscription: boolean
  plan_type: string | null
  calls_used: number
  calls_limit: number | null
  calls_remaining: number
  credits_used: number
  credits_limit: number | null
  credits_remaining: number
  wallet_balance_cents?: number
  wallet_auto_load_enabled?: boolean
  wallet_auto_load_threshold_cents?: number
  wallet_auto_load_amount_cents?: number
  billing_interval: string | null
  current_period_end: string | null
  status: string | null
  is_trial?: boolean
  trial_start_date?: string | null
  trial_end_date?: string | null
  trial_days_remaining?: number | null
}

export interface NexusWalletTransaction {
  id: string
  subscription_id: string
  user_id: string
  transaction_type: 'top_up' | 'overage_debit' | 'auto_load' | 'manual_adjustment'
  amount_cents: number
  balance_after_cents: number
  description: string
  stripe_checkout_session_id?: string | null
  stripe_payment_intent_id?: string | null
  metadata: Record<string, any>
  created_at: string
}

export interface NexusCreditChargeResult {
  success: boolean
  reason?: string
  credits_used_this_month?: number
  overage_credits?: number
  overage_charge_cents?: number
  wallet_balance_cents?: number
}

/** Use service client on server; browser client only on client (never load browser client in server bundle). */
function getClient(): SupabaseClient<Database> {
  if (typeof window === "undefined") {
    return createServiceClient()
  }
  return require("@/lib/supabase/browser-client").supabase as SupabaseClient<Database>
}

/** Client cast for nexus_subscriptions and RPCs not in generated Database types. */
function getNexusClient(): any {
  return getClient()
}

// Subscription CRUD operations

export async function createNexusSubscription(subscriptionData: Omit<NexusSubscription, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await getNexusClient()
    .from('nexus_subscriptions')
    .insert(subscriptionData)
    .select()
    .single()

  if (error) {
    console.error('Error creating Nexus subscription:', error)
    throw error
  }

  return data as NexusSubscription
}

export async function getNexusSubscription(userId: string): Promise<NexusSubscription | null> {
  const { data, error } = await getNexusClient()
    .from('nexus_subscriptions')
    .select('*')
    .eq('user_id', userId)
    .in('status', ['active', 'trialing'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    console.error('Error fetching Nexus subscription:', error)
    throw error
  }

  return data ?? null
}

export async function getNexusSubscriptionById(
  subscriptionId: string
): Promise<NexusSubscription | null> {
  const { data, error } = await getNexusClient()
    .from('nexus_subscriptions')
    .select('*')
    .eq('id', subscriptionId)
    .maybeSingle()

  if (error) {
    console.error('Error fetching Nexus subscription by ID:', error)
    throw error
  }

  return (data as NexusSubscription | null) ?? null
}

export async function updateNexusSubscription(
  subscriptionId: string,
  updates: Partial<NexusSubscription>
) {
  const { data, error } = await getNexusClient()
    .from('nexus_subscriptions')
    .update(updates as Record<string, unknown>)
    .eq('id', subscriptionId)
    .select()
    .single()

  if (error) {
    console.error('Error updating Nexus subscription:', error)
    throw error
  }

  return data as NexusSubscription
}

function getWalletRetentionExpiryDate(params: {
  currentPeriodEnd: string
  cancelAtPeriodEnd: boolean
}) {
  const baseDate = params.cancelAtPeriodEnd
    ? new Date(params.currentPeriodEnd)
    : new Date()

  baseDate.setDate(baseDate.getDate() + 90)
  return baseDate.toISOString()
}

export async function cancelNexusSubscription(subscriptionId: string, cancelAtPeriodEnd = true) {
  const existingSubscription = await getNexusSubscriptionById(subscriptionId)

  if (!existingSubscription) {
    throw new Error('Subscription not found')
  }

  const nextMetadata = {
    ...(existingSubscription.metadata || {}),
    wallet_retention_expires_at: getWalletRetentionExpiryDate({
      currentPeriodEnd: existingSubscription.current_period_end,
      cancelAtPeriodEnd
    }),
    wallet_retention_policy: 'reserved_for_90_days_after_service_end'
  }

  const { data, error } = await getNexusClient()
    .from('nexus_subscriptions')
    .update({
      cancel_at_period_end: cancelAtPeriodEnd,
      status: cancelAtPeriodEnd ? 'active' : 'canceled',
      metadata: nextMetadata,
      updated_at: new Date().toISOString()
    })
    .eq('id', subscriptionId)
    .select()
    .single()

  if (error) {
    console.error('Error canceling Nexus subscription:', error)
    throw error
  }

  return data as NexusSubscription
}

// Usage tracking functions

export async function getNexusUsageStats(userId: string): Promise<NexusUsageStats> {
  const { data, error } = await getNexusClient()
    .rpc('get_nexus_usage_stats', { user_uuid: userId })

  if (error) {
    console.error('Error fetching Nexus usage stats:', error)
    throw error
  }

  return data as NexusUsageStats
}

export async function incrementNexusCallUsage(userId: string): Promise<boolean> {
  const { data, error } = await getNexusClient()
    .rpc('increment_nexus_call_usage', { user_uuid: userId })

  if (error) {
    console.error('Error incrementing Nexus call usage:', error)
    throw error
  }

  return data as boolean
}

export async function getNexusCreditsUsage(userId: string): Promise<{ credits_used: number; credits_limit: number | null; credits_remaining: number }> {
  const stats = await getNexusUsageStats(userId)
  return {
    credits_used: stats.credits_used || 0,
    credits_limit: stats.credits_limit,
    credits_remaining: stats.credits_remaining || 0
  }
}

export async function getNexusWalletTransactions(
  userId: string,
  limit: number = 20
): Promise<NexusWalletTransaction[]> {
  const { data, error } = await getNexusClient()
    .from('nexus_wallet_transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) {
    console.error('Error fetching Nexus wallet transactions:', error)
    throw error
  }

  return (data ?? []) as NexusWalletTransaction[]
}

export async function addNexusWalletFunds(params: {
  subscriptionId: string
  amountCents: number
  transactionType: NexusWalletTransaction['transaction_type']
  description: string
  stripeCheckoutSessionId?: string | null
  stripePaymentIntentId?: string | null
  metadata?: Record<string, unknown>
}) {
  const {
    subscriptionId,
    amountCents,
    transactionType,
    description,
    stripeCheckoutSessionId = null,
    stripePaymentIntentId = null,
    metadata = {}
  } = params

  const { data, error } = await getNexusClient().rpc('add_nexus_wallet_funds', {
    subscription_uuid: subscriptionId,
    amount_cents: amountCents,
    transaction_type_param: transactionType,
    description_param: description,
    stripe_checkout_session_id_param: stripeCheckoutSessionId,
    stripe_payment_intent_id_param: stripePaymentIntentId,
    metadata_param: metadata
  })

  if (error) {
    console.error('Error adding Nexus wallet funds:', error)
    throw error
  }

  return data as {
    success: boolean
    balance_cents: number
    transaction_id: string
  }
}

export async function chargeNexusCredits(params: {
  userId: string
  credits: number
  description?: string
  metadata?: Record<string, unknown>
}): Promise<NexusCreditChargeResult> {
  const {
    userId,
    credits,
    description = 'Nexus overage usage',
    metadata = {}
  } = params

  const { data, error } = await getNexusClient().rpc('charge_nexus_credits', {
    user_uuid: userId,
    credits_amount: credits,
    description_param: description,
    metadata_param: metadata
  })

  if (error) {
    console.error('Error charging Nexus credits:', error)
    throw error
  }

  return data as NexusCreditChargeResult
}

export async function updateNexusWalletSettings(params: {
  subscriptionId: string
  walletAutoLoadEnabled?: boolean
  walletAutoLoadThresholdCents?: number
  walletAutoLoadAmountCents?: number
}) {
  const { subscriptionId, walletAutoLoadEnabled, walletAutoLoadThresholdCents, walletAutoLoadAmountCents } = params

  const updates: Record<string, unknown> = {}

  if (typeof walletAutoLoadEnabled === 'boolean') {
    updates.wallet_auto_load_enabled = walletAutoLoadEnabled
  }

  if (typeof walletAutoLoadThresholdCents === 'number') {
    updates.wallet_auto_load_threshold_cents = walletAutoLoadThresholdCents
  }

  if (typeof walletAutoLoadAmountCents === 'number') {
    updates.wallet_auto_load_amount_cents = walletAutoLoadAmountCents
  }

  updates.updated_at = new Date().toISOString()

  const { data, error } = await getNexusClient()
    .from('nexus_subscriptions')
    .update(updates)
    .eq('id', subscriptionId)
    .select('*')
    .single()

  if (error) {
    console.error('Error updating Nexus wallet settings:', error)
    throw error
  }

  return data as NexusSubscription
}

export async function incrementNexusCredits(userId: string, credits: number): Promise<boolean> {
  const { data, error } = await getNexusClient()
    .rpc('increment_nexus_credits_usage', {
      user_uuid: userId,
      credits_amount: credits
    })

  if (error) {
    console.error('Error incrementing Nexus credits:', error)
    throw error
  }

  return data as boolean
}

export async function checkNexusCreditsLimit(userId: string, requiredCredits: number): Promise<boolean> {
  const { data, error } = await getNexusClient()
    .rpc('check_nexus_credits_limit', {
      user_uuid: userId,
      required_credits: requiredCredits
    })

  if (error) {
    console.error('Error checking Nexus credits limit:', error)
    throw error
  }

  return data as boolean
}

// Analytics and reporting

export async function getNexusSubscriptionAnalytics(userId?: string) {
  let query = getNexusClient()
    .from("nexus_subscriptions")
    .select("*")

  if (userId) {
    query = query.eq('user_id', userId)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching Nexus analytics:', error)
    throw error
  }

  return data
}

// Utility functions

export function getPlanLimits(planType: string) {
  switch (planType) {
    case NEXUS_PLAN_STARTER:
      return {
        callsPerMonth: NEXUS_PRICING.starter.callsPerMonth,
        integrations: NEXUS_PRICING.starter.integrations,
        aiReceptionists: NEXUS_PRICING.starter.aiReceptionists
      }
    case NEXUS_PLAN_PROFESSIONAL:
      return {
        callsPerMonth: NEXUS_PRICING.professional.callsPerMonth,
        integrations: NEXUS_PRICING.professional.integrations,
        aiReceptionists: NEXUS_PRICING.professional.aiReceptionists
      }
    case NEXUS_PLAN_ENTERPRISE:
      return {
        callsPerMonth: NEXUS_PRICING.enterprise.callsPerMonth,
        integrations: NEXUS_PRICING.enterprise.integrations,
        aiReceptionists: NEXUS_PRICING.enterprise.aiReceptionists
      }
    default:
      return null
  }
}

export function calculateSavings(monthlyPrice: number, yearlyPrice: number): number {
  const yearlyCost = monthlyPrice * 12
  return Math.round(((yearlyCost - yearlyPrice) / yearlyCost) * 100)
}
